import React from 'react';
import { autobind, throttle } from 'core-decorators';
import './Picture.less';

/**
 * 参数详解
 */
interface IProps {
	className?: string;
	// 图片地址
	src: string;
	// 是否需要占位符
	placeholder?: boolean;
	// 是否需要lazy load，需设置占位符才生效
	lazyLoad?: boolean;
	// 阿里云图片缩放对象，具体参数见https://www.alibabacloud.com/help/zh/doc-detail/44688.htm
	resize?: any;
	// 阿里云图片裁剪对象，具体参数见https://www.alibabacloud.com/help/zh/doc-detail/44693.htm?spm=a2c63.p38356.b99.577.1f617ba7ijH3Zx
	crop?: any;
	// 阿里云图片内切圆对象，具体参数见https://www.alibabacloud.com/help/zh/doc-detail/44695.htm
	circle?: any;
	// 阿里云图片圆角矩形对象，具体参数见https://www.alibabacloud.com/help/zh/doc-detail/44694.htm?spm=a2c63.p38356.b99.579.2d5b6c55SDHA7S
	'rounded-corners'?: any;
	// componentDidMount时把当前组件传入此方法，供外部使用
	innerRef?: any;

	onLoad?: (e: any) => void;
	onError?: (e: any) => void;
	onClick?: (e: any) => void;
}

/**
 * 用于使兼容webp的浏览器使用webp
 *
 */
function getSearchParams(name: string, s?: string): any {
	let search: any = (s || window.location.search).match(/\?.*(?=\b|#)/);
	if (search) {
		search = search[0].replace(/^\?/, '');
	}
	if (!search) return name ? '' : {};
	let queries: any = {};
	let params: any = search.split('&');

	for (let i of Object.keys(params)) {
		let param: string[] = params[i].split('=');
		queries[param[0]] = param[1] ? decodeURIComponent(param[1]) : '';
	}

	return name ? queries[name] : queries;
}

/**
 * 图片组件
 */
@autobind
class Picture extends React.PureComponent<IProps> {
	private pictureRef: any;
	private imgRef: any;

	public state: any = {
		src: '',
		webpSrc: '',
		isLoaded: false,

		catchError: false,
	};

	private data: any = {
		boundingClientRectTop: null,
	};

	/**
	 * componentWillReceiveProps
	 */
	public componentWillReceiveProps(nextProps: any): any {
		if (nextProps.src && nextProps.src !== this.props.src) {
			const src: any = this.parseSrc(nextProps.src);
			const webpSrc: any = this.addFormatWebp(src);
			this.setState({
				src,
				webpSrc,
			});
		}
	}

	/**
	 * componentDidMount
	 */
	public async componentDidMount(): Promise<any> {
		if (this.props.innerRef) {
			this.props.innerRef(this);
		}
		if (this.props.src) {
			if (this.props.placeholder && this.props.lazyLoad) {
				// 用占位符时才lazyload
				window.addEventListener('touchmove', this.lazyload);
				window.addEventListener('mousewheel', this.lazyload);
				window.addEventListener('mousedown', this.lazyload);
				window.addEventListener('mouseup', this.lazyload);
				this.lazyload();
			} else {
				this.loadImg();
			}
		}
	}

	/**
	 * componentWillUnmount
	 */
	public componentWillUnmount(): void {
		window.removeEventListener('touchmove', this.lazyload);
		window.removeEventListener('mousewheel', this.lazyload);
		window.removeEventListener('mousedown', this.lazyload);
		window.removeEventListener('mouseup', this.lazyload);
	}

	/**
	 * 懒加载
	 */
	@throttle(200)
	public lazyload(): void {
		if (this.data.lazyloadRedetect) {
			clearTimeout(this.data.lazyloadRedetect);
		}
		if (!this.pictureRef) {
			return;
		}
		const pictureTop: number = this.pictureRef.getBoundingClientRect().top;
		if (pictureTop !== this.data.boundingClientRectTop) {
			this.data.boundingClientRectTop = pictureTop;
			if (this.hasEnteredViewport(pictureTop)) {
				this.loadImg();
				if (this.data.lazyloadRedetect) {
					clearTimeout(this.data.lazyloadRedetect);
				}
				window.removeEventListener('touchmove', this.lazyload);
				window.removeEventListener('mousewheel', this.lazyload);
				window.removeEventListener('mousedown', this.lazyload);
				window.removeEventListener('mouseup', this.lazyload);
			} else {
				// 因为滑动有惯性，所以再检测一遍直到惯性停止
				this.data.lazyloadRedetect = setTimeout((): void => {
					this.data.lazyloadRedetect = null;
					this.lazyload();
				}, 600);
			}
		}
	}

	/**
	 * loadImg
	 */
	public loadImg(): void {
		const src: any = this.parseSrc(this.props.src);
		const webpSrc: any = this.addFormatWebp(src);
		this.setState({
			src,
			webpSrc,
		});
	}

	/**
	 * hasEnteredViewport
	 */
	public hasEnteredViewport(pictureTop: number): boolean {
		// const pictureTop = this.pictureRef.getBoundingClientRect().top;
		// 上下偏移值
		const offset: number = 200;

		return pictureTop > 0 - offset && pictureTop < window.innerHeight + offset;
	}

	/**
	 * onLoad
	 */
	public onLoad(e: any): void {
		if (this.props.onLoad) {
			this.props.onLoad(e);
		}
		this.setState({
			isLoaded: true,
		});
	}

	/**
	 * onError
	 */
	public onError(e: any): void {
		if (this.props.onError) {
			this.props.onError(e);
		}
	}

	/**
	 * pictureClickHandle
	 */
	public pictureClickHandle(e: any): void {
		if (this.props.onClick) {
			this.props.onClick(e);
		}
	}

	/**
	 * addFormatWebp
	 */
	public addFormatWebp(src: string): any {
		try {
			const url: any = new URL(src);
			url.protocol = location.protocol;
			if (!url.host.includes('img.qlchat.com')) {
				return null;
			}
			const ossProcess: any = url.searchParams.get('x-oss-process');
			if (!ossProcess) {
				url.searchParams.set('x-oss-process', 'image/format,webp');
			} else if (ossProcess.includes('image') && !ossProcess.includes('/format')) {
				url.searchParams.set('x-oss-process', `${ossProcess}/format,webp`);
			}
			url.search = decodeURIComponent(url.search);

			return url.toString();
		} catch (e) {
			return null;
		}
	}

	/**
	 * parseOssProcess
	 */
	public parseOssProcess(ossProcess: any): any {
		if (!ossProcess) return '';
		const processArr: any[] = ossProcess.split('/');
		const originOssProcess: any = {};
		processArr.forEach((p: any, i: number): void => {
			if (i === 0) return null;
			const pArr: any[] = p.split(',');
			if (!originOssProcess[pArr[0]]) {
				originOssProcess[pArr[0]] = {};
			}
			pArr.forEach((p2: any, i2: number): void => {
				if (i2 === 0) return null;
				const p2Arr: any[] = p2.split('_');
				if (p2Arr.length === 1) {
					originOssProcess[pArr[0]] = p2Arr[0];
				} else {
					originOssProcess[pArr[0]][p2Arr[0]] = p2Arr[1];
				}
			});
		});

		return originOssProcess;
	}

	/**
	 * stringifyOssProcess
	 */
	public stringifyOssProcess(ossProcess: any): any {
		const processArr: string[] = ['image'];
		for (let k of Object.keys(ossProcess)) {
			processArr.push(`/${k}`);
			if (Object.prototype.toString.call(ossProcess[k]) === '[object Object]') {
				for (let j of Object.keys(ossProcess[k])) {
					processArr.push(`,${j}_${ossProcess[k][j]}`);
				}
			} else {
				processArr.push(`,${ossProcess[k]}`);
			}
		}
		if (processArr.length === 1) {
			return '';
		} else {
			return processArr.join('');
		}
	}

	/**
	 * stringifyOssProcess
	 */
	public parseSrc(src: string): any {
		try {
			const url: any = new URL(src);
			// const originOssProcess = getSearchParams('x-oss-process', url.search);
			const originOssProcess: any = url.searchParams.get('x-oss-process');
			if (!url.host.includes('img.qlchat.com')) {
				return src;
			}

			const ossProcess: any = originOssProcess ? this.parseOssProcess(originOssProcess) : {};

			const resize: any = { m: 'fill', limit: '0', ...ossProcess.resize, ...this.props.resize };
			// 从旧参数里提取resize的w和h
			url.pathname = url.pathname.replace(/\@((.*){0,})/, (p: any, $1: any): any => {
				if (!resize.w && !resize.h && $1) {
					let w: any = $1.match(/(\d{0,})w/);
					let h: any = $1.match(/(\d{0,})h/);
					w = w ? w[1] : '';
					h = h ? h[1] : '';
					resize.w = w;
					resize.h = h;
				}

				return '';
			});
			// w和h都没有的时候，不能使用resize
			if (resize.w || resize.h) {
				ossProcess.resize = resize;
			}

			// 裁剪
			if (this.props.crop) {
				ossProcess.crop = this.props.crop;
			}

			// 内切圆
			if (this.props.circle) {
				ossProcess.circle = this.props.circle;
			}

			// 圆角
			if (this.props['rounded-corners']) {
				ossProcess['rounded-corners'] = this.props['rounded-corners'];
			}

			const ossProcessStr: any = this.stringifyOssProcess(ossProcess);

			if (ossProcessStr) {
				// url.search = setSearchParams({'x-oss-process': ossProcessStr}, url.search);
				url.searchParams.set('x-oss-process', ossProcessStr);
				url.search = decodeURIComponent(url.search);
			}

			return url.toString();
		} catch (err) {
			return src;
		}
	}

	/**
	 * 渲染
	 */
	public render(): any {
		return (
			<picture className={`ql-feat-picture${this.props.className ? ` ${this.props.className}` : ''}${this.props.placeholder && !this.state.isLoaded ? ' picture-placeholder' : ''}`} onClick={this.pictureClickHandle} ref={(r: any): void => (this.pictureRef = r)} data-log-region={this.props['data-log-region'] || ''} data-log-pos={this.props['data-log-pos'] || ''} data-log-type={this.props['data-log-type'] || ''} data-log-name={this.props['data-log-name'] || ''}>
				{this.state.webpSrc && <source type="image/webp" srcSet={this.state.webpSrc} />}
				{this.state.src && <img ref={(r: any): void => (this.imgRef = r)} src={this.state.src} onLoad={this.onLoad} onError={this.onError} style={{ display: this.state.isLoaded ? '' : 'none' }} />}
			</picture>
		);
	}
}

export default Picture;
